import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Cell,
  Column,
  HeaderGroup,
  TableHeaderProps,
  usePagination,
  useSortBy,
  useTable,
  SortingRule,
  useGlobalFilter,
  useFilters,
  Filters,
  useFlexLayout,
} from "react-table";
import { SortDescendingIcon, SortAscendingIcon } from "..";
import { IIconProps, SearchNoResultsIcon } from "../icons/Icons";
import "./UsersTable.scss";
import { UserListData } from "../../pages/userManagement/UserManagementUtils";
import { Pagination } from "./Pagination";
import { Button } from "../../components";
import { useTranslation } from "react-i18next";
import { GetReadOnlyStatus } from "../../utils/readOnlyUser";

// eslint-disable-next-line @typescript-eslint/ban-types
interface UsersTableProps<T extends object = {}> {
  columns: Column<T>[];
  data: T[];
  columnToSortOnRender?: string;
  numberOfPagesToShowInNavigation?: number;
  sortTableData: (sortBy: SortingRule<UserListData>[]) => void;
  headerIconsName: string;
  EditUserIcon: (props: IIconProps) => JSX.Element;
  onEditUserIconClick: (param: string) => void;
  controlledFilters: Filters<T>;
  setFilters: (filters: Filters<T>) => void;
  pageIndex: number;
  pageCount: number;
  setPageIndex: (pageIndex: number) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UsersTable = (props: UsersTableProps<any>) => {
  const {
    columns,
    data,
    columnToSortOnRender,
    headerIconsName,
    EditUserIcon,
    onEditUserIconClick,
    sortTableData,
    numberOfPagesToShowInNavigation = 1,
    controlledFilters,
    setFilters,
    pageIndex: controlledPageIndex = 0,
    pageCount,
    setPageIndex,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleButton = () => {
    navigate("/user-management/create-user");
  };

  const loggedInUserIsReadOnly = GetReadOnlyStatus();
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: controlledPageIndex,
        filters: controlledFilters,
      },
      manualSortBy: true,
      manualFilters: true,
      manualPagination: true,
      pageCount: pageCount,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useFlexLayout,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canNextPage,
    canPreviousPage,
    nextPage,
    gotoPage,
    previousPage,
    pageOptions,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    state: { pageIndex, sortBy, filters },
  } = tableInstance;

  useEffect(() => {
    sortTableData(sortBy);
  }, [sortBy, sortTableData]);

  useEffect(() => {
    setFilters(filters);
  }, [filters, setFilters]);

  useEffect(() => {
    headerGroups.forEach((headerGroup) =>
      headerGroup.headers.filter((n) => {
        if (n.Header === columnToSortOnRender) {
          n.toggleSortBy();
        }
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customGoToPage = useCallback(
    (page: number) => {
      setPageIndex(page);
      gotoPage(page);
    },
    [gotoPage, setPageIndex],
  );

  const customNextPage = useCallback(() => {
    setPageIndex(pageIndex + 1);
    nextPage();
  }, [nextPage, pageIndex, setPageIndex]);

  const customPreviousPage = useCallback(() => {
    setPageIndex(pageIndex - 1);
    previousPage();
  }, [pageIndex, previousPage, setPageIndex]);

  const renderColumnHeader = useCallback(
    (column: HeaderGroup<object>) => {
      const { key, ...restHeaderProps }: TableHeaderProps = column.getHeaderProps(column.getSortByToggleProps());
      const headerName = column.Header;

      return (
        <th key={key} {...restHeaderProps} data-testid={`table-header-${headerName?.toString() ?? ""}`} className="th">
          <div>
            {headerName === headerIconsName ? (
              ""
            ) : (
              <>
                {headerName}
                {column.canSort ? (
                  <span>
                    {column.isSorted ? column.isSortedDesc ? <SortDescendingIcon /> : <SortAscendingIcon /> : ""}
                  </span>
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
          <div>{column.canFilter ? column.render("Filter") : <></>}</div>
        </th>
      );
    },
    [headerIconsName],
  );

  const renderColumnCell = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (cell: Cell<object, any>) => {
      const { key, ...restCellProps } = cell.getCellProps();

      return (
        <td key={key} {...restCellProps}>
          {cell.column.Header === headerIconsName ? (
            <EditUserIcon
              onClick={() => onEditUserIconClick(cell.value as string)}
              className="edit-user-icon"
              dataTestId="edit-user"
            />
          ) : (
            cell.render("Cell")
          )}
        </td>
      );
    },
    [EditUserIcon, headerIconsName, onEditUserIconClick],
  );

  return (
    <section className="table-container">
      <main>
        <table className="table" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...restHeaderGroupProps }: TableHeaderProps = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...restHeaderGroupProps}>
                  {headerGroup.headers.map((column) => {
                    return renderColumnHeader(column);
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const { key, ...restRowProps } = row.getRowProps();
              return (
                <tr key={key} {...restRowProps} data-testid="table-single-row">
                  {row.cells.map((cell) => {
                    return renderColumnCell(cell);
                  })}
                </tr>
              );
            })}
            {data.length === 0 ? (
              <tr>
                <td colSpan={1000} className="no-results">
                  <div>
                    <p>{t("noResultsForYourSearch")}</p>
                    <SearchNoResultsIcon />
                  </div>
                </td>
              </tr>
            ) : (
              <></>
            )}
          </tbody>
        </table>
      </main>

      <footer>
        <Pagination
          pageIndex={pageIndex}
          maxPagesToRender={numberOfPagesToShowInNavigation}
          totalPages={pageOptions}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          gotoPage={customGoToPage}
          nextPage={customNextPage}
          previousPage={customPreviousPage}
        />
        {!loggedInUserIsReadOnly ? (
        <div className="user-management-button">
          <Button
            className="button-medium button-bulk-upload"
            isDisabled={false}
            lKey={t("buttonBulkUpload")}
            onClick={() => navigate("/bulk-upload")}
            type="button"
          />
          <Button
            className="button-medium primary-color"
            isDisabled={false}
            lKey={t("buttonNewUser")}
            onClick={handleButton}
            type="button"
          />
        </div>
        ) : (
          <></>
          )}
      </footer>
    </section>
  );
};
